import { useState, useEffect, useContext } from "react";
import "./questionCatalogForm.scss";
import { useNavigate, useParams } from "react-router-dom";
import { notification, Modal } from "antd";
import axios from "axios";

// context
import { AuthContext } from "../../contexts/authContext";

// icon
import { addIcon, deletIcon } from "../../constants/icons";

// util
import monitorInputChange from "../../utils/monitorInputChange";
import handleStringToNum from "../../utils/handleStringToNum";

// interface
import { ErrorParams } from "../../interfaces/errorParams";

interface QuestionData {
  question: string;
  justification: string;
  comment: string;
}

interface SubSectionData {
  _id: string;
  title: string;
  questions: QuestionData[];
}

interface SectionData {
  _id: string;
  sequence: number;
  section: string;
  description: string;
  color: string;
  bg_color: string;
  question_color: string;
  questions: QuestionData[];
}

type NotificationType = "success" | "info" | "warning" | "error";

const API_URL = "/api/v1";

const QuestionCatalogForm = ({ edit }: { edit: boolean }) => {
  const {setUser} = useContext(AuthContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [api, contextHolder] = notification.useNotification();
  const [section, setSection] = useState<SectionData>({
    _id: "",
    sequence: 0,
    section: "",
    description: "",
    color: "#3D424A",
    bg_color: "#3D424A",
    question_color: "#3D424A",
    questions: [],
  });
  const [subSection, setSubsection] = useState<SubSectionData[]>([]);
  const [errorParams, setErrorParams] = useState<ErrorParams[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [delModal, setDelModal] = useState<boolean>(false);

  const opNotification = (
    type: NotificationType,
    message: string,
    descripton: string
  ) => {
    api[type]({
      message: message,
      description: descripton,
    });
  };

  const handleAddSectionQuestion = () => {
    setSection({
      ...section,
      questions: [
        ...section.questions,
        {
          question: "",
          justification: "",
          comment: "",
        },
      ],
    });
  };

  const handleDeleteSectionQuestion = (questionIndex: number) => {
    setSection({
      ...section,
      questions: section.questions.filter((ques, qindex) => {
        return qindex !== questionIndex;
      }),
    });
  };

  const handleAddSubSection = () => {
    setSubsection([
      ...subSection,
      {
        _id:"",
        title: "",
        questions: [
          {
            question: "",
            justification: "",
            comment: "",
          },
        ],
      },
    ]);
  };

  const handleDeleteSubSection = (subsectionIndex: number) => {
    setSubsection(
      subSection.filter((subsec, index) => {
        return index !== subsectionIndex;
      })
    );
  };

  const handleAddSubsectionQuestion = (subsectionIndex: number) => {
    setSubsection(
      subSection.map((subsec, index) => {
        if (index === subsectionIndex) {
          return {
            ...subsec,
            questions: [
              ...subsec.questions,
              {
                question: "",
                justification: "",
                comment: "",
              },
            ],
          };
        } else {
          return subsec;
        }
      })
    );
  };

  const handleDeleteSubsectionQuestion = (
    subsectionIndex: number,
    questionIndex: number
  ) => {
    setSubsection(
      subSection.map((subsec, index) => {
        if (index === subsectionIndex) {
          let updatedQuestions = subsec.questions.filter((ques, qindex) => {
            return qindex !== questionIndex;
          });
          return {
            _id:subsec._id,
            title: subsec.title,
            questions: updatedQuestions,
          };
        } else {
          return subsec;
        }
      })
    );
  };

  const handleFormValidation = () => {
    let errorlist = [];

    // section validation
    if (section.section === "" || section.section.length < 2) {
      errorlist.push({
        path: "section",
        message: "Section name should be atleast 2 characters",
      });
    }
    if (section.description === "" || section.description.length < 3) {
      errorlist.push({
        path: "description",
        message: "Description should be atleast 3 characters",
      });
    }
    if (section.color === "" || section.color.length < 6) {
      errorlist.push({
        path: "color",
        message: "Enter valid hex color code",
      });
    }
    if (section.bg_color === "" || section.bg_color.length < 6) {
      errorlist.push({
        path: "bg_color",
        message: "Enter valid hex color code",
      });
    }
    if (section.question_color === "" || section.question_color.length < 6) {
      errorlist.push({
        path: "question_color",
        message: "Enter valid hex color code",
      });
    }
    if (section.questions.length > 0) {
      section.questions.forEach((question, index) => {
        if (question.question === "" || question.question.length < 2) {
          errorlist.push({
            path: `section-question-${index}`,
            message: "Question should be atleast 2 characters",
          });
        }
        if (
          question.justification === "" ||
          question.justification.length < 1
        ) {
          errorlist.push({
            path: `section-justification-${index}`,
            message: "Justification be atleast 1 character",
          });
        }
      });
    } else if (section.questions.length === 0 && subSection.length === 0) {
      opNotification(
        "warning",
        "Questions Missing",
        "Add atleast 1 question or create sub-sections"
      );
      errorlist.push({
        path: "question-missing",
        message: "Sub-section requires minimum of 1 question",
      });
    } else if (subSection.length > 0) {
      subSection.forEach((subsec, index) => {
        if (subsec.title === "" || subsec.title.length < 2) {
          errorlist.push({
            path: `subsection-title-${index}`,
          });
        }
        if (subsec.questions.length === 0) {
          opNotification(
            "warning",
            "Sub-section Question Missing",
            `Add atleast 1 question for the sub-section #${index + 1}`
          );
          errorlist.push({
            path: "sub-section-question-missing",
            message: "Sub-section requires minimum of 1 question",
          });
        } else {
          subsec.questions.forEach((ques, qidx) => {
            if (ques.question === "" || ques.question.length < 2) {
              errorlist.push({
                path: `subsection-question-${qidx}`,
                message: "Question should be atleast 2 characters",
              });
            }
            if (ques.justification === "" || ques.justification.length < 1) {
              errorlist.push({
                path: `subsection-justification-${qidx}`,
                message: "Justification should be atleast 1 character",
              });
            }
          });
        }
      });
    }
    setErrorParams(errorlist);
    if (errorlist.length > 0) {
      return false;
    } else {
      return true;
    }
  };

  const handleCreateSection = async () => {
    try {
      setLoading(true);
      let validation = handleFormValidation();
      if (!validation) {
        return false;
      }

      const res = await axios.post(`${API_URL}/catalog/create`, {
        section,
        subSection,
      });
      if (res.status === 200 && res.data.success === true) {
        navigate("/dashboard/catalog?action=create&status=success");
      }
    } catch (error:any) {
      console.log("Error creating section:", error);
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFetchSectionInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/catalog/info/${id}`);
      if (res.status === 200 && res.data.success === true) {
        // console.log(res.data.section)
        setSection({
          _id: res.data.section._id.toString(),
          sequence: res.data.section.sequence,
          section: res.data.section.section,
          description: res.data.section.description,
          color: res.data.section.color,
          bg_color: res.data.section.bg_color,
          question_color: res.data.section.question_color,
          questions: res.data.section.questions,
        });
        setSubsection(res.data.section.sub_sections);
      }
    } catch (error:any) {
      console.log("Error fetching section info", error);
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
    }
  };

  const handleUpdateSection = async () => {
    try {
      setLoading(true);
      const validation = handleFormValidation();
      if (!validation) {
        return false;
      }

      const res = await axios.put(`${API_URL}/catalog/update`, {
        section,
        subSection,
      });
      if (res.status === 200 && res.data.success === true) {
        navigate("/dashboard/catalog?action=update&status=success");
      }
    } catch (error:any) {
      console.log("Error updating section", error);
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSection = async () => {
    try {
      setLoading(true);
      const res = await axios.delete(`${API_URL}/catalog/delete/${id}`);
      if (res.status === 200 && res.data.success === true) {
        navigate("/dashboard/catalog?action=delete&status=success");
      }
    } catch (error:any) {
      console.log("Error deleting section", error);
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (errorParams.length > 0) {
      errorParams.forEach((err) => {
        if (
          err.path !== "question-missing" &&
          err.path !== "sub-section-question-missing"
        ) {
          let ele = document.getElementById(err.path);
          if (ele) {
            ele.style.borderColor = "#FF7875";
          }
        }
      });
    }
  }, [errorParams]);

  useEffect(() => {
    if (edit) {
      handleFetchSectionInfo();
    }
    // eslint-disable-next-line
  }, [edit]);

  return (
    <form
      className="questionCatalogForm"
      onSubmit={(e) => {
        e.preventDefault();
        // eslint-disable-next-line
        {
          edit ? handleUpdateSection() : handleCreateSection();
        }
      }}
    >
      {contextHolder}
      <div className="formBody">
        <div className="inputContainer">
          <div className="componentContainer">
            <div className="componentTitle">
              <span>Section</span>
            </div>
            <div className="componentBody">
              <div className="sectionTitleContainer">
                <div className="sectionTitleInputHolder">
                  <span>Section Name</span>
                  <input
                    id="section"
                    type="text"
                    value={section.section}
                    onChange={(e) => {
                      setSection({
                        ...section,
                        section: e.target.value,
                      });
                      monitorInputChange("section");
                    }}
                  />
                </div>
                <div className="sectionSequenceHolder">
                  <span>Sequence</span>
                  <input
                    type="number"
                    id="sequence"
                    value={section.sequence}
                    onChange={(e) => {
                      let val = handleStringToNum(e.target.value);
                      setSection({
                        ...section,
                        sequence: val,
                      });
                    }}
                  />
                </div>
              </div>
              <div className="sectionTitleContainer">
                <div className="sectionTitleInputHolder">
                  <span>Description</span>
                  <textarea
                    id="description"
                    value={section.description}
                    onChange={(e) => {
                      setSection({
                        ...section,
                        description: e.target.value,
                      });
                      monitorInputChange("description");
                    }}
                  />
                </div>
              </div>
              <div className="sectionTitleContainer">
                <div className="sectionColorInputHolder">
                  <span>Section Color</span>
                  <input
                    type="text"
                    id="color"
                    placeholder="#FFFFFF"
                    value={section.color}
                    onChange={(e) => {
                      setSection({
                        ...section,
                        color: e.target.value,
                      });
                      monitorInputChange("color");
                    }}
                  />
                </div>
                <div className="sectionColorInputHolder">
                  <span>Background Color</span>
                  <input
                    type="text"
                    id="bg_color"
                    placeholder="#FFFFFF"
                    value={section.bg_color}
                    onChange={(e) => {
                      setSection({
                        ...section,
                        bg_color: e.target.value,
                      });
                      monitorInputChange("bg_color");
                    }}
                  />
                </div>
                <div className="sectionColorInputHolder">
                  <span>Question Color</span>
                  <input
                    type="text"
                    id="question_color"
                    placeholder="#FFFFFF"
                    value={section.question_color}
                    onChange={(e) => {
                      setSection({
                        ...section,
                        question_color: e.target.value,
                      });
                      monitorInputChange("question_color");
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        {section.questions.length > 0 &&
          section.questions.map((question, index) => (
            <div className="componentContainer" key={index}>
              <div className="componentTitle">
                <span>Q{index + 1}</span>

                <span className="subSecBtnHolder">
                  <div
                    className="btn"
                    onClick={() => {
                      handleDeleteSectionQuestion(index);
                    }}
                  >
                    {deletIcon}
                  </div>
                </span>
              </div>
              <div className="componentBody">
                <div className="questionInputHolder">
                  <input
                    type="text"
                    id={`section-question-${index}`}
                    value={question.question}
                    placeholder="Question"
                    onChange={(e) => {
                      setSection({
                        ...section,
                        questions: section.questions.map((ques, qidx) => {
                          if (qidx === index) {
                            return {
                              ...ques,
                              question: e.target.value,
                            };
                          } else {
                            return ques;
                          }
                        }),
                      });
                      monitorInputChange(`section-question-${index}`);
                    }}
                  />
                </div>
                <div
                  className="questionInputHolder"
                  style={{
                    marginBottom: "10px",
                  }}
                >
                  <textarea
                    id={`section-justification-${index}`}
                    value={question.justification}
                    placeholder="Justification"
                    onChange={(e) => {
                      setSection({
                        ...section,
                        questions: section.questions.map((ques, qidx) => {
                          if (qidx === index) {
                            return {
                              ...ques,
                              justification: e.target.value,
                            };
                          } else {
                            return ques;
                          }
                        }),
                      });
                      monitorInputChange(`section-justification-${index}`);
                    }}
                  />
                </div>
              </div>
            </div>
          ))}

        {/* component to be mounted when the user adds sub-sections */}
        {subSection.length > 0 &&
          subSection.map((subsec, subsecIndex) => (
            <div className="componentContainer" key={subsecIndex}>
              <div className="componentTitle">
                <span>Sub-Section #{subsecIndex + 1}</span>

                <span className="subSecBtnHolder">
                  <div
                    className="btn"
                    onClick={() => {
                      handleDeleteSubSection(subsecIndex);
                    }}
                  >
                    {deletIcon}
                  </div>
                </span>
              </div>
              <div className="componentBody">
                <div className="subsectionContainer">
                  <div className="titleInputHolder">
                    <span>Sub-section title</span>
                    <input
                      id={`subsection-title-${subsecIndex}`}
                      type="text"
                      value={subsec.title}
                      onChange={(e) => {
                        setSubsection(
                          subSection.map((ss, ssidx) => {
                            if (ssidx === subsecIndex) {
                              let data = {
                                _id:ss._id,
                                title: e.target.value,
                                questions: ss.questions,
                              };
                              return data;
                            } else {
                              return ss;
                            }
                          })
                        );
                        monitorInputChange(`subsection-title-${subsecIndex}`);
                      }}
                    />
                  </div>
                  {subsec.questions.map((question, questionIndex) => (
                    <div
                      className="questionComponentHolder"
                      key={questionIndex}
                    >
                      <div className="questionInputHolder">
                        <div className="labelHolder">
                          <span>
                            Q{subsecIndex + 1}.{questionIndex + 1}
                          </span>
                          <div
                            className="btn"
                            onClick={() => {
                              handleDeleteSubsectionQuestion(
                                subsecIndex,
                                questionIndex
                              );
                            }}
                          >
                            {deletIcon}
                          </div>
                        </div>
                        <input
                          id={`subsection-question-${questionIndex}`}
                          type="text"
                          placeholder="Question"
                          value={question.question}
                          onChange={(e) => {
                            let subquestions = subsec.questions;
                            let updatedQuestions = subquestions.map(
                              (ques, qidx) => {
                                if (qidx === questionIndex) {
                                  return {
                                    ...ques,
                                    question: e.target.value,
                                  };
                                } else {
                                  return ques;
                                }
                              }
                            );

                            setSubsection(
                              subSection.map((ss, ssidx) => {
                                if (ssidx === subsecIndex) {
                                  return {
                                    _id:ss._id,
                                    title: ss.title,
                                    questions: updatedQuestions,
                                  };
                                } else {
                                  return ss;
                                }
                              })
                            );
                            monitorInputChange(
                              `subsection-question-${questionIndex}`
                            );
                          }}
                        />
                      </div>
                      <div className="questionInputHolder">
                        {/* <span>Justification</span> */}
                        <textarea
                          id={`subsection-justification-${questionIndex}`}
                          placeholder="Justification"
                          value={question.justification}
                          onChange={(e) => {
                            let subquestions = subsec.questions;
                            let updatedQuestions = subquestions.map(
                              (ques, qidx) => {
                                if (qidx === questionIndex) {
                                  return {
                                    ...ques,
                                    justification: e.target.value,
                                  };
                                } else {
                                  return ques;
                                }
                              }
                            );

                            setSubsection(
                              subSection.map((ss, ssidx) => {
                                if (ssidx === subsecIndex) {
                                  return {
                                    _id:ss._id,
                                    title: ss.title,
                                    questions: updatedQuestions,
                                  };
                                } else {
                                  return ss;
                                }
                              })
                            );
                            monitorInputChange(
                              `subsection-justification-${questionIndex}`
                            );
                          }}
                        />
                      </div>
                    </div>
                  ))}

                  <div className="addQuestion">
                    <span
                      onClick={() => {
                        handleAddSubsectionQuestion(subsecIndex);
                      }}
                    >
                      {addIcon} Add Question
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}

        {/* action buttons */}
        <div className="sectionActionBtnHolder">
          {section.questions.length === 0 && (
            <span onClick={handleAddSubSection}>{addIcon} Add Sub-section</span>
          )}
          {subSection.length === 0 && (
            <span onClick={handleAddSectionQuestion}>
              {addIcon} Add Question
            </span>
          )}
        </div>
      </div>
      <div className="formFooter">
        {edit === true && (
          <div className="deleteButtonHolder">
            <button
              type="button"
              className="buttonDelete"
              onClick={() => {
                setDelModal(true);
              }}
            >
              {deletIcon} Delete
            </button>
          </div>
        )}

        <button
          type="button"
          className="buttonSecondary"
          onClick={() => {
            navigate("/dashboard/catalog");
          }}
        >
          Cancel
        </button>
        <button className="buttonPrimary" disabled={loading ? true : false}>
          Submit
        </button>
      </div>
      <Modal
        open={delModal}
        closable={false}
        centered
        onCancel={() => {
          setDelModal(false);
        }}
        onOk={handleDeleteSection}
        okText="Delete"
        okButtonProps={{
          type: "primary",
          style: {
            backgroundColor: "#FF4D4F",
          },
        }}
        footer={null}
      >
        <div className="deleteCatalogContainer">
          <span className="header">Delete Questionnaire</span>
          <p>Do you want to delete this Questionnaire Section?</p>
          <div className="buttonHolder">
            <button
              onClick={()=>{
                setDelModal(false);
              }}
            >Cancel</button>
            <button className="btnDelete"
              onClick={handleDeleteSection}
            >Delete</button>
          </div>
        </div>
      </Modal>
    </form>
  );
};

export default QuestionCatalogForm;
